import React, { useState, useContext, useEffect } from "react";
import {
    Button, Typography, CardHeader, CardContent, Card, Grid,
    makeStyles, Container, TextField, DialogContentText, IconButton,
    Dialog, DialogTitle, DialogContent, DialogActions, InputLabel, Select, FormControl,
    MenuItem, Radio, RadioGroup, FormControlLabel, FormLabel
} from "@material-ui/core";
import { Theme } from '@material-ui/core/styles';
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import InfoIcon from "@material-ui/icons/Info";
import { useHistory } from "react-router-dom";
import moment from "moment";
import FooterSection from "./Footer";
import { ToastContext } from "../Common/ToastProvider";
import { getRole, getUserName } from "../Common/utils/auth";
import { oldCustomerApi, CustomerData } from "../api/oldCustomer";

// Types
interface SearchState {
    regNo: string;
    mobile: string;
    aadhaar: string;
    consumerNo: string;
    mainAgent: string;
    familyAadhaar: string;
}

interface AgentListItem {
    label: string;
    value: string;
}

// Styles
const useStyles = makeStyles((theme: Theme) => ({
    root: {
        margin: theme.spacing(1),
        width: "25ch",
        flexGrow: 1,
    },
    form: {
        width: "100%",
        marginTop: theme.spacing(1),
    },
    cardGrid: {
        paddingBottom: theme.spacing(8),
    },
    card: {
        height: "100%",
        display: "flex",
        flexDirection: "column",
    },
    cardContent: {
        flexGrow: 1,
        padding: theme.spacing(2),
    },
    formControl: {
        minWidth: "100%",
    },
    selectEmpty: {
        marginTop: theme.spacing(2),
    },
    searchContainer: {
        marginTop: theme.spacing(4),
        marginBottom: theme.spacing(4),
    },
    searchButton: {
        margin: theme.spacing(2),
    },
    customerCard: {
        marginTop: theme.spacing(4),
    },
    textField: {
        marginBottom: theme.spacing(2),
    }
}));

const OldDataManagement: React.FC = () => {
    const classes = useStyles();
    const history = useHistory();
    const { showToast } = useContext(ToastContext);
    const CHARACTER_LIMIT = 13;

    // State
    const [users, setUsers] = useState<CustomerData[]>([]);
    const [searchState, setSearchState] = useState<SearchState>({
        regNo: "",
        mobile: "",
        aadhaar: "",
        consumerNo: "",
        mainAgent: "",
        familyAadhaar: ""
    });
    const [selectedCustomer, setSelectedCustomer] = useState<CustomerData | null>(null);
    const [agentList, setAgentList] = useState<AgentListItem[]>([]);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [userName, setUserName] = useState("");

    // Auth helpers from utils
    const getUserRole = getRole;
    const isSuperAdmin = () => getUserRole() === "superadmin";

    // Event Handlers
    const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchState(prev => ({
            ...prev,
            [event.target.name]: event.target.value
        }));
    };

    const handleCustomerInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!selectedCustomer) return;
        setSelectedCustomer({
            ...selectedCustomer,
            [event.target.name]: event.target.value
        });
    };

    const handleSearch = async (event: React.FormEvent) => {
        event.preventDefault();
        try {
            let result;
            if (searchState.mobile) {
                result = await oldCustomerApi.findCustomer({
                    findkey: "mobile",
                    mobile: searchState.mobile
                });
            } else if (searchState.aadhaar) {
                result = await oldCustomerApi.findCustomer({
                    findkey: "mainAadhaar",
                    mainAadhaar: searchState.aadhaar
                });
            } else if (searchState.consumerNo) {
                result = await oldCustomerApi.findCustomer({
                    findkey: "consumerNo",
                    consumerNo: searchState.consumerNo
                });
            }
            if (result) handleSearchResult(result);
        } catch (error) {
            showToast("Something went wrong", "error");
        }
    };

    const handleSearchResult = (result: any) => {
        if (!result.data || result.data.length === 0) {
            showToast("No result found", "error");
            return;
        }
        setUsers(result.data);
        setSelectedCustomer(result.data[0]);
    };

    const handleUpdate = async () => {
        if (!selectedCustomer) return;

        try {
            const result = await oldCustomerApi.updateCustomer(selectedCustomer);
            if (result.data) {
                showToast("Customer updated successfully", "success");
                setOpenEditDialog(false);
            }
        } catch (error: any) {
            showToast(error.response?.data?.message || "Update failed", "error");
        }
    };

    const handleDelete = async () => {
        if (!selectedCustomer?._id) return;

        try {
            const result = await oldCustomerApi.deleteCustomer(selectedCustomer._id);
            if (result) {
                showToast("Customer deleted successfully", "success");
                setOpenDeleteDialog(false);
                setUsers(users.filter(user => user._id !== selectedCustomer._id));
            }
        } catch (error: any) {
            showToast(error.response?.data?.message || "Delete failed", "error");
        }
    };

    // Effects
    useEffect(() => {
        document.title = "Customer | Jaman HP Gas";
        setUserName(getUserName());

        const fetchAgents = async () => {
            try {
                const result = await oldCustomerApi.getActiveAgents();
                if (result.data.agents.length > 0) {
                    setAgentList(
                        result.data.agents.map((agent) => ({
                            label: agent.name,
                            value: agent.name
                        }))
                    );
                }
            } catch (error: any) {
                showToast(error.response?.data?.message || "Failed to fetch agents", "error");
            }
        };

        fetchAgents();
    }, []);

    return (
        <React.Fragment>
            <Container maxWidth="md" component="main">
                <Typography variant="h4" align="center" className={classes.searchContainer}>
                    <InfoIcon fontSize="small" color="error" />
                    Legacy Customer Management (Pre-2021)
                </Typography>

                {/* Search Form */}
                <Grid container spacing={3} className={classes.searchContainer}>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Main Aadhaar No"
                            variant="outlined"
                            fullWidth
                            name="aadhaar"
                            value={searchState.aadhaar}
                            onChange={handleSearchInputChange}
                            inputProps={{ maxLength: CHARACTER_LIMIT }}
                            className={classes.textField}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Mobile No"
                            variant="outlined"
                            fullWidth
                            name="mobile"
                            value={searchState.mobile}
                            onChange={handleSearchInputChange}
                            inputProps={{ maxLength: 10 }}
                            className={classes.textField}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Consumer No"
                            variant="outlined"
                            fullWidth
                            name="consumerNo"
                            value={searchState.consumerNo}
                            onChange={handleSearchInputChange}
                            className={classes.textField}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={handleSearch}
                            className={classes.searchButton}
                        >
                            Find Customer
                        </Button>
                    </Grid>
                </Grid>

                {/* Results Grid */}
                <Grid container spacing={4}>
                    {users.map((user) => (
                        <Grid item xs={12} sm={6} key={user._id}>
                            {getUserRole() === "user" && user.mainAgent === userName ? (
                                <CustomerCard
                                    customer={user}
                                    onEdit={() => {
                                        setSelectedCustomer(user);
                                        setOpenEditDialog(true);
                                    }}
                                    onDelete={() => {
                                        setSelectedCustomer(user);
                                        setOpenDeleteDialog(true);
                                    }}
                                    isAdmin={isSuperAdmin()}
                                />
                            ) : getUserRole() === "user" ? (
                                <Card style={{ backgroundColor: "#009688" }}>
                                    <CardContent>
                                        <Typography color="secondary">
                                            CUSTOMER REGISTERED WITH OTHER AGENT
                                        </Typography>
                                    </CardContent>
                                </Card>
                            ) : (
                                <CustomerCard
                                    customer={user}
                                    onEdit={() => {
                                        setSelectedCustomer(user);
                                        setOpenEditDialog(true);
                                    }}
                                    onDelete={() => {
                                        setSelectedCustomer(user);
                                        setOpenDeleteDialog(true);
                                    }}
                                    isAdmin={isSuperAdmin()}
                                />
                            )}
                        </Grid>
                    ))}
                </Grid>

                {/* Edit Dialog */}
                <Dialog
                    open={openEditDialog}
                    onClose={() => setOpenEditDialog(false)}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle>Update Customer Data</DialogTitle>
                    <DialogContent dividers>
                        {selectedCustomer && (
                            <EditCustomerForm
                                customer={selectedCustomer}
                                onChange={handleCustomerInputChange}
                                agentList={agentList}
                                isAdmin={isSuperAdmin()}
                            />
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenEditDialog(false)} color="primary">
                            Cancel
                        </Button>
                        <Button onClick={handleUpdate} color="primary" variant="contained">
                            Save Changes
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Delete Confirmation Dialog */}
                <Dialog
                    open={openDeleteDialog}
                    onClose={() => setOpenDeleteDialog(false)}
                >
                    <DialogContent>
                        <DialogContentText>
                            Are you sure you want to remove this customer?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenDeleteDialog(false)} color="primary">
                            No
                        </Button>
                        <Button onClick={handleDelete} color="primary" autoFocus>
                            Yes
                        </Button>
                    </DialogActions>
                </Dialog>
            </Container>
            <FooterSection />
        </React.Fragment>
    );
};

// Sub-components
interface CustomerCardProps {
    customer: CustomerData;
    onEdit: () => void;
    onDelete: () => void;
    isAdmin: boolean;
}

const CustomerCard: React.FC<CustomerCardProps> = ({
    customer,
    onEdit,
    onDelete,
    isAdmin
}) => {
    const classes = useStyles();

    return (
        <Card className={classes.card}>
            <CardContent>
                <CardHeader
                    action={
                        <>
                            {customer.installtatus === "Complete" && (
                                <IconButton>
                                    <CheckCircleIcon color="primary" />
                                </IconButton>
                            )}
                            {isAdmin && (
                                <>
                                    <IconButton onClick={onEdit}>
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton onClick={onDelete}>
                                        <DeleteIcon />
                                    </IconButton>
                                </>
                            )}
                        </>
                    }
                    title={customer.name.toUpperCase() || "NA"}
                />
                <Typography>Name: {customer.name.toUpperCase() || "NA"}</Typography>
                <Typography>Main Aadhaar: {customer.mainAadhaar}</Typography>
                <Typography>Mobile No: {customer.mobile}</Typography>
                <Typography>Registration No: {customer.regNo || "NA"}</Typography>
                <Typography>Consumer No: {customer.consumerNo || "NA"}</Typography>
                <Typography color="primary">
                    Registered Agency: {customer.registeredAgencyName || "NA"}
                </Typography>
                <Typography color="primary">
                    Main Agent: {customer.mainAgent || "NA"}
                </Typography>
                <Typography>Old Agent: {customer.oldAgentName || "NA"}</Typography>
                <Typography>Sub Agent: {customer.subAgent || "NA"}</Typography>
                <Typography>Remarks: {customer.remarks || "NA"}</Typography>
                <Typography>
                    Created: {moment(customer.createdAt).format('LLL') || "NA"}
                </Typography>
                {customer.updatedAt && (
                    <Typography>
                        Updated: {moment(customer.updatedAt).format('LLL')}
                    </Typography>
                )}
                <Typography>Year: {customer.year || "NA"}</Typography>
                <Typography>Added By: {customer.addedBy || "NA"}</Typography>
                {customer.InstalationLetter && (
                    <Typography color="primary">
                        Installation: {customer.installtatus}
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
};

interface EditCustomerFormProps {
    customer: CustomerData;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    agentList: AgentListItem[];
    isAdmin: boolean;
}

const EditCustomerForm: React.FC<EditCustomerFormProps> = ({
    customer,
    onChange,
    agentList,
    isAdmin
}) => {
    const classes = useStyles();

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <TextField
                    label="Name"
                    name="name"
                    variant="outlined"
                    fullWidth
                    value={customer.name}
                    onChange={onChange}
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                    label="Main Aadhaar"
                    name="mainAadhaar"
                    variant="outlined"
                    fullWidth
                    value={customer.mainAadhaar}
                    onChange={onChange}
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                    label="Mobile"
                    name="mobile"
                    variant="outlined"
                    fullWidth
                    type="number"
                    value={customer.mobile}
                    onChange={onChange}
                    inputProps={{
                        maxLength: 10
                    }}
                />
            </Grid>
            {customer.regNo && (
                <Grid item xs={12}>
                    <TextField
                        label="Registration No"
                        name="regNo"
                        variant="outlined"
                        fullWidth
                        value={customer.regNo}
                        onChange={onChange}
                    />
                </Grid>
            )}
            <Grid item xs={12}>
                <TextField
                    label="Consumer No"
                    name="consumerNo"
                    variant="outlined"
                    fullWidth
                    value={customer.consumerNo}
                    onChange={onChange}
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                    label="Registered Agency Name"
                    name="registeredAgencyName"
                    variant="outlined"
                    fullWidth
                    value={customer.registeredAgencyName}
                    onChange={onChange}
                />
            </Grid>

            {isAdmin && (
                <Grid item xs={12}>
                    <FormControl variant="outlined" fullWidth>
                        <InputLabel>Update Main Agent</InputLabel>
                        <Select
                            name="mainAgent"
                            value={customer.mainAgent}
                            onChange={onChange as any}
                            label="Update Main Agent"
                        >
                            {agentList.map(agent => (
                                <MenuItem key={agent.value} value={agent.value}>
                                    {agent.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
            )}

            <Grid item xs={12}>
                <TextField
                    label="Old Agent Name"
                    name="oldAgentName"
                    variant="outlined"
                    fullWidth
                    value={customer.oldAgentName}
                    onChange={onChange}
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                    label="Sub Agent"
                    name="subAgent"
                    variant="outlined"
                    fullWidth
                    value={customer.subAgent}
                    onChange={onChange}
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                    label="Remarks"
                    name="remarks"
                    variant="outlined"
                    fullWidth
                    value={customer.remarks}
                    onChange={onChange}
                />
            </Grid>

            {isAdmin && (
                <Grid item xs={12}>
                    <FormControl component="fieldset">
                        <FormLabel>Installation Status</FormLabel>
                        <RadioGroup
                            name="installtatus"
                            value={customer.installtatus}
                            onChange={onChange}
                            row
                        >
                            <FormControlLabel
                                value="Not Complete"
                                control={<Radio />}
                                label="Not Complete"
                            />
                            <FormControlLabel
                                value="Complete"
                                control={<Radio />}
                                label="Complete"
                            />
                        </RadioGroup>
                    </FormControl>
                </Grid>
            )}

            <Grid item xs={12}>
                <FormControl component="fieldset">
                    <FormLabel>Year</FormLabel>
                    <RadioGroup
                        name="year"
                        value={customer.year}
                        onChange={onChange}
                        row
                    >
                        {["2017", "2018", "2019", "2020"].map(year => (
                            <FormControlLabel
                                key={year}
                                value={year}
                                control={<Radio />}
                                label={year}
                            />
                        ))}
                    </RadioGroup>
                </FormControl>
            </Grid>
        </Grid>
    );
};

export default OldDataManagement;
